import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { getAllQuestions } from './questionService';
import { getVersionByNumber } from './versionService';
import { computeScanResults } from './scoringEngine';
import { computeRecommendations } from './bonusRulesEngine';

/**
 * Scan Service
 * 
 * Manages assessment scan submissions and retrieval.
 */

/**
 * Validates scan answers against question snapshot
 * @param {Object} answers - User answers
 * @param {Array} questions - Question snapshot
 * @throws {Error} If validation fails
 */
function validateScanAnswers(answers, questions) {
  const questionMap = new Map(questions.map(q => [q.key, q]));

  // Check all required questions are answered
  const requiredQuestions = questions.filter(q => q.type === 'scored');
  const missingAnswers = requiredQuestions.filter(q => answers[q.key] === undefined);

  if (missingAnswers.length > 0) {
    throw new Error(`Missing answers for required questions: ${missingAnswers.map(q => q.key).join(', ')}`);
  }

  // Validate each answer
  Object.entries(answers).forEach(([key, value]) => {
    const question = questionMap.get(key);

    if (!question) {
      throw new Error(`Invalid question key: ${key}`);
    }

    if (question.type === 'scored') {
      if (typeof value !== 'number' || value < 1 || value > (question.maxScore || 5)) {
        throw new Error(`Invalid answer for ${key}: must be between 1 and ${question.maxScore || 5}`);
      }
    } else if (question.type === 'bonus') {
      if (question.bonusChoices && question.bonusChoices.length > 0) {
        if (!question.bonusChoices.includes(value)) {
          throw new Error(`Invalid answer for ${key}: must be one of ${question.bonusChoices.join(', ')}`);
        }
      }
    }
  });
}

/**
 * Submits a new scan
 * @param {Object} scanData - Scan data object
 * @param {Object} scanData.answers - User answers
 * @param {number} scanData.questionsVersion - Version number used
 * @param {string} [scanData.userId] - User ID (null for anonymous)
 * @param {Object} [scanData.metadata] - Additional metadata
 * @param {boolean} [scanData.isTest] - Whether this is a test scan (not saved)
 * @returns {Promise<Object>} Scan results
 */
export async function submitScan(scanData) {
  const {
    answers,
    questionsVersion,
    userId = null,
    metadata = {},
    isTest = false
  } = scanData;

  if (!db && !isTest) {
    throw new Error('Firestore is not configured');
  }

  try {
    let questions;
    let totalMaxScore;

    // Handle draft version (0) or published version
    if (questionsVersion === 0) {
      questions = await getAllQuestions({ activeOnly: true });
      // Calculate max score for draft questions
      totalMaxScore = questions.reduce((sum, q) => {
        if (q.type === 'scored') {
          return sum + ((q.maxScore || 5) * (q.weight || 1));
        }
        return sum;
      }, 0);
    } else {
      // Get question snapshot for this version
      const version = await getVersionByNumber(questionsVersion);
      questions = version.questionsSnapshot;
      totalMaxScore = version.totalMaxScore;
    }

    // Validate answers
    validateScanAnswers(answers, questions);

    // Separate scored and bonus answers
    const scoredAnswers = {};
    const bonusAnswers = {};

    questions.forEach(q => {
      if (answers[q.key] !== undefined) {
        if (q.type === 'scored') {
          scoredAnswers[q.key] = answers[q.key];
        } else {
          bonusAnswers[q.key] = answers[q.key];
        }
      }
    });

    // Compute scores
    const scoringResults = computeScanResults(
      scoredAnswers,
      questions,
      totalMaxScore
    );

    // Apply bonus rules
    const recommendationResults = computeRecommendations(
      scoringResults.zone,
      bonusAnswers
    );

    // Prepare scan document
    const scanDoc = {
      userId,
      questionsVersion,
      scanAt: serverTimestamp(),
      language: metadata.language || 'en',
      answers,
      computed: {
        rawScores: scoringResults.rawScores,
        totalRaw: scoringResults.totalRaw,
        normalizedTotal: scoringResults.normalizedTotal,
        zone: scoringResults.zone,
        recommendations: recommendationResults.recommendations,
        bonusRulesApplied: recommendationResults.triggeredRules
      },
      metadata: {
        device: metadata.device || 'web',
        duration: metadata.duration || 0,
        ipAddress: metadata.ipAddress || null
      }
    };

    // If test mode, return results without saving
    if (isTest) {
      return {
        ...scanDoc,
        scanAt: new Date(),
        isTest: true
      };
    }

    // Save to Firestore
    const docRef = await addDoc(collection(db, 'scans'), scanDoc);

    return {
      id: docRef.id,
      ...scanDoc,
      scanAt: new Date()
    };
  } catch (error) {
    console.error('Error submitting scan:', error);
    throw error;
  }
}

/**
 * Gets a scan by ID
 * @param {string} scanId - Scan document ID
 * @returns {Promise<Object>} Scan data
 */
export async function getScanById(scanId) {
  if (!db) {
    throw new Error('Firestore is not configured');
  }

  try {
    const scanRef = doc(db, 'scans', scanId);
    const scanSnap = await getDoc(scanRef);

    if (!scanSnap.exists()) {
      throw new Error(`Scan not found: ${scanId}`);
    }

    return {
      id: scanSnap.id,
      ...scanSnap.data()
    };
  } catch (error) {
    console.error('Error getting scan:', error);
    throw error;
  }
}

/**
 * Gets scans for a specific user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Array of scans
 */
export async function getUserScans(userId) {
  if (!db) {
    console.warn('Firestore is not configured');
    return [];
  }

  try {
    const scansRef = collection(db, 'scans');
    const q = query(
      scansRef,
      where('userId', '==', userId),
      orderBy('scanAt', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting user scans:', error);
    throw error;
  }
}

/**
 * Gets all scans (admin only)
 * @param {Object} filters - Filter options
 * @returns {Promise<Array>} Array of scans
 */
export async function getAllScans(filters = {}) {
  if (!db) {
    console.warn('Firestore is not configured');
    return [];
  }

  try {
    const scansRef = collection(db, 'scans');
    const constraints = [];

    if (filters.questionsVersion) {
      constraints.push(where('questionsVersion', '==', filters.questionsVersion));
    }

    constraints.push(orderBy('scanAt', 'desc'));

    const q = query(scansRef, ...constraints);
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting all scans:', error);
    throw error;
  }
}
